import api from './api';
import { putObject } from './uploadPhotos';

/*
 * Sending an issue report (routes/issue_reports.py): the report first, so the
 * words are saved whatever happens to the pictures, then each screenshot by
 * presign -> PUT to storage -> register, the way a photograph goes up.
 */
export const MAX_SCREENSHOTS = 3;
export const MAX_BYTES = 5 * 1024 * 1024;
export const MAX_COMMENT = 5000;
export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Why a file cannot be a screenshot ('type' or 'size'), or null. */
export function screenshotProblem(file) {
  if (!file || !SCREENSHOT_TYPES.includes(file.type)) return 'type';
  if (!file.size || file.size > MAX_BYTES) return 'size';
  return null;
}

/** Where the person is: the path and query, never the origin. */
export function currentPage(location = typeof window !== 'undefined' ? window.location : null) {
  if (!location) return null;
  return `${location.pathname || '/'}${location.search || ''}`;
}

/** The always-present Report an issue tab (components/issues/ReportIssueTab.js),
 *  which dialogs add to their Tab cycle (utils/focusTrap.js trapTab `extra`). */
export const REPORT_TAB_SELECTOR = '[data-report-issue-tab]';
export const reportTab = (doc = typeof document !== 'undefined' ? document : null) =>
  doc?.querySelector(REPORT_TAB_SELECTOR) || null;

const MAX_TITLE = 300;
const textOf = el => (el?.textContent || '').replace(/\s+/g, ' ').trim();

/** The accessible name of a dialog: its aria-label, else the text its
 *  aria-labelledby names, else its first heading. */
export function dialogName(dialog, doc = document) {
  if (!dialog) return '';
  const label = (dialog.getAttribute('aria-label') || '').trim();
  if (label) return label;
  const ids = (dialog.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
  const named = ids.map(id => textOf(doc.getElementById(id))).filter(Boolean).join(' ');
  return named || textOf(dialog.querySelector('h1, h2, h3'));
}

/**
 * Which screen a report is about, read when the report is opened (before its
 * own dialog is on the page): the dialog in front -- the one under the middle
 * of the window, whatever order the page holds them in -- else the last open
 * modal dialog, else the page's main heading, else the document's title.
 */
export function screenContext(doc = typeof document !== 'undefined' ? document : null,
  win = typeof window !== 'undefined' ? window : null) {
  if (!doc) return null;
  const notReport = el => el && !el.closest('[data-report-issue]');
  let dialog = null;
  if (win && typeof doc.elementFromPoint === 'function') {
    const front = doc.elementFromPoint(win.innerWidth / 2, win.innerHeight / 2);
    const found = front?.closest?.('[role="dialog"]');
    if (notReport(found)) dialog = found;
  }
  if (!dialog) {
    const open = [...doc.querySelectorAll('[role="dialog"][aria-modal="true"]')].filter(notReport);
    dialog = open[open.length - 1] || null;
  }
  const name = (dialog ? dialogName(dialog, doc) : '') || textOf(doc.querySelector('main h1, h1')) || doc.title || '';
  return name.slice(0, MAX_TITLE) || null;
}

export function viewportSize(win = typeof window !== 'undefined' ? window : null) {
  if (!win?.innerWidth || !win?.innerHeight) return null;
  return `${Math.round(win.innerWidth)}x${Math.round(win.innerHeight)}`;
}

/**
 * Send the report, then its screenshots. Resolves to {reportId, failed}:
 * `failed` names the screenshots that did not get there (the report itself
 * did). Rejects only when the report could not be sent.
 */
export async function submitIssueReport({ comment, pagePath, pageTitle, appVersion, viewport, files = [] }) {
  const { data } = await api.post('/issue-reports', {
    comment, page_path: pagePath, page_title: pageTitle, app_version: appVersion, viewport,
  });
  const reportId = data.report_id;
  const failed = [];
  for (const file of files.slice(0, MAX_SCREENSHOTS)) {
    try {
      const { data: issued } = await api.post(`/issue-reports/${reportId}/screenshots/presign`,
        { content_type: file.type, size: file.size });
      await putObject(issued.presign_url, file, issued.content_type);
      await api.post(`/issue-reports/${reportId}/screenshots`, { s3_key: issued.s3_key, size: file.size });
    } catch (e) {
      failed.push(file.name || 'screenshot');
    }
  }
  return { reportId, failed };
}
